import { defineStore } from 'pinia';
import { loadSubscription } from '@apps-once/request/load-subscription';
import { getActiveUserCount } from '@apps-once/request/active-user-count';
import { useEventBus } from '@deputyapp/webapp-sdk';
import {
  ADD_ON_STATUS,
  SUBSCRIPTION,
  SUBSCRIPTION_NAME,
} from '@apps-once/definitions';
import { getAddOnTrialDaysRemaining } from '@apps-billing-shared/util/get-add-on-trial-days-remaining';
import { getOnceUrl } from '@apps-billing-shared/util/get-once-url';
import {
  ADD_ON_SUBSCRIPTION_TYPE,
  addOnDataMap,
  addOns,
  allAddOnDetails,
  OnceRoutes,
  SUBSCRIPTION_NAME_TRACKING,
  TrialLength,
} from '@apps-billing-shared/definitions';
import { addDaysToDate } from '@apps-billing-shared/util/add-days-to-date';
import { formatDisplayDateShort } from '@apps-billing-shared/util/format-display-date-short';
import { useOnceStore } from '@apps-once/store';
import {
  SubscriptionData,
  AddOnProduct,
} from '@apps-once/types/subscription.d';
import { useAddOnStore } from '@apps-once/store/add-on';
import EVENTS from '../events';

type SubscriptionStoreState = {
  status: {
    isLoading: boolean;
    isPurchaseAdditionalUsersModalVisible: boolean;
    subscriptionModel: any;
    isSuccessPurchaseAdditionalUsersModalVisible: boolean;
    isSuccessSwitchToAnnualModalVisible: boolean;
    isRenewAnnualSubscriptionModalVisible: boolean;
    isSuccessRenewAnnualSubscriptionModalVisible: boolean;
    isChoosePlanModalVisible: boolean;
  };
  error: {
    loading: null | Error;
  };
  data: Record<string, SubscriptionData>;
  loadingPromises: Record<string, Promise<void> | undefined>;
};

export const useSubscriptionStore = defineStore('once:subscription', {
  state(): SubscriptionStoreState {
    return {
      status: {
        isLoading: false,
        isPurchaseAdditionalUsersModalVisible: false,
        subscriptionModel: {},
        isSuccessPurchaseAdditionalUsersModalVisible: false,
        isSuccessSwitchToAnnualModalVisible: false,
        isRenewAnnualSubscriptionModalVisible: false,
        isSuccessRenewAnnualSubscriptionModalVisible: false,
        isChoosePlanModalVisible: false,
      },
      error: {
        loading: null,
      },
      data: {},
      loadingPromises: {},
    };
  },
  getters: {
    getCurrentPlanId(state: SubscriptionStoreState) {
      return (hostname: string): SubscriptionData => state.data[hostname];
    },
    getPlanNextBillingDate(state: SubscriptionStoreState) {
      return (hostname: string): string =>
        state.data[hostname]?.NextBillingDate;
    },
    getRecentlyConvertedFromAnnualToMonthly(state: SubscriptionStoreState) {
      return (hostname: string): boolean =>
        state.data[hostname]?.recentlyConvertedFromAnnualToMonthly;
    },
    getAnnualSubscriptionStartDate(state: SubscriptionStoreState) {
      return (hostname: string): string =>
        state.data[hostname]?.SubscriptionStartDate;
    },
    getAnnualSubscriptionEndDate(state: SubscriptionStoreState) {
      return (hostname: string): string =>
        state.data[hostname]?.SubscriptionEndDate;
    },
    getConversionDate(state: SubscriptionStoreState) {
      return (hostname: string): string | undefined =>
        state.data[hostname]?.ConversionDate;
    },
    getCsmName(state: SubscriptionStoreState) {
      return (hostname: string): string | null | undefined =>
        state.data[hostname]?.AccountContext?.CsmName;
    },
    getCsmEmail(state: SubscriptionStoreState) {
      return (hostname: string): string | null | undefined =>
        state.data[hostname]?.AccountContext?.CsmEmail;
    },
    isAnnualSubscriptionEndingWithinNext31Days() {
      return (hostname: string): boolean => {
        const subscriptionEndDate =
          useSubscriptionStore().getAnnualSubscriptionEndDate(hostname);
        if (!subscriptionEndDate) {
          return false;
        }

        const endDate = new Date(subscriptionEndDate);
        const todayDate = new Date();
        const today = todayDate.getTime();

        // parse the feature flag value to number
        const days = Number(
          useOnceStore().getFeatureFlag('wo-2104-annual-renewal-config-days')
        );

        const startOfRenewalPeriod = new Date(subscriptionEndDate);
        startOfRenewalPeriod.setDate(startOfRenewalPeriod.getDate() - days);

        return (
          today > startOfRenewalPeriod.getTime() && today < endDate.getTime()
        );
      };
    },
    hasPaidSubscription(state: SubscriptionStoreState) {
      return (hostname: string): boolean => {
        const freeSubscriptionTypes: number[] = [
          SUBSCRIPTION.FREE,
          SUBSCRIPTION.FREEMIUM,
          SUBSCRIPTION.PRO_TRIAL,
        ];

        const subscriptionType = state.data[hostname]?.SubscriptionType;

        return (
          typeof subscriptionType === 'number' &&
          !freeSubscriptionTypes.includes(subscriptionType)
        );
      };
    },
    getTrialLength() {
      return (addOnId: string): TrialLength => {
        return addOnDataMap[addOnId]?.trialLength;
      };
    },
    getTrialPeriod() {
      return (addOnId: string): string => {
        const trialLength = this.getTrialLength(addOnId);
        if (trialLength) {
          return `${trialLength.count} ${trialLength.unit}`;
        }
        return '';
      };
    },
    getTrialDisplayBillingDate() {
      return (addOnId: keyof typeof addOns): string => {
        if (typeof addOnId !== 'string') {
          throw new Error('Invalid addOnId type');
        }

        const now = new Date();
        return formatDisplayDateShort(
          addDaysToDate(now, addOnDataMap[addOnId]?.trialLength?.count)
        );
      };
    },
    getChangePlanUrl() {
      return (planId: string): string => {
        const onceStore = useOnceStore();
        return `${OnceRoutes.Base}${OnceRoutes.ChangePlan.replace(
          ':installationId',
          onceStore.id
        ).replace(':planId', planId)}`;
      };
    },
    getAioBundleUrl() {
      // Env-aware host (not the hardcoded OnceRoutes.Base) so the redirect stays
      // same-origin on every environment. The bundle-upsell carry-over relies on
      // in-memory Pinia state surviving this navigation, which only holds for a
      // same-document hash change — a cross-origin jump to prod would wipe it.
      return (planId: string): string => {
        const onceStore = useOnceStore();
        const path = OnceRoutes.ChangePlan.replace(
          ':installationId',
          onceStore.id
        ).replace(':planId', planId);
        return `${getOnceUrl()}${path}/aio_bundle`;
      };
    },

    // Retrieves the number of users that were pre-purchased for an active annual base plan subscription.
    // **Note:** This applies only to currently subscribed plans and should **not** be used during checkout.
    getPrePurchasedUserCount(
      state: SubscriptionStoreState
    ): (hostname: string) => number {
      return (hostname: string): number => {
        const prePurchasedCount =
          state.data[hostname]?.EMPLOYEE_QUANTITY_PREPURCHASE;
        if (typeof prePurchasedCount === 'undefined') {
          console.warn(
            `EMPLOYEE_QUANTITY_PREPURCHASE is not set for hostname: ${hostname}`
          );
          return 4; // Default value
        }
        return prePurchasedCount;
      };
    },
  },
  actions: {
    /**
     * Loads subscription data for a specific hostname
     *
     * @param {string} hostname - The hostname to load subscription for
     * @param {number} planId - The plan ID
     * @param {boolean} [forceReload=false] - Whether to force reload the data
     * @return {Promise<void>}
     */
    async loadSubscription(
      hostname: string,
      planId: number,
      forceReload = false
    ): Promise<void> {
      // Check if data is already cached
      if (this.data[hostname] && !forceReload) {
        // Emit subscription data to subscribers in legacy frontend
        await this.emitSubscriptionToLegacy(hostname, this.data[hostname]);
        return;
      }

      // Check if there's already a loading promise for this hostname
      if (this.loadingPromises[hostname] && !forceReload) {
        return this.loadingPromises[hostname];
      }

      // Create and cache the loading promise
      const loadingPromise = this._loadSubscriptionData(hostname, planId);
      this.loadingPromises[hostname] = loadingPromise;

      try {
        await loadingPromise;
      } finally {
        delete this.loadingPromises[hostname];
      }
    },

    /**
     * Ensures subscription data is loaded for a hostname.
     * Checks if data exists, waits for existing load, or logs error if neither.
     * This is used when we just need to ensure data is loaded (e.g., by loadBusiness)
     * without triggering a new load ourselves.
     *
     * @param {string} hostname - The hostname to ensure subscription data for
     * @return {Promise<void>} Resolves when subscription data is available
     */
    async ensureSubscriptionDataLoaded(hostname: string): Promise<void> {
      if (!hostname) {
        return;
      }

      // If data already exists, we're done
      if (this.data[hostname]) {
        return;
      }

      // If there's an existing load in progress, wait for it
      if (this.loadingPromises[hostname]) {
        await this.loadingPromises[hostname];
        return;
      }

      // If we get here, no load was triggered - this shouldn't happen if loadBusiness was called
      console.warn(
        'Subscription data not loading for hostname:',
        hostname,
        '- loadBusiness should have been called first'
      );
    },

    /**
     * Internal method to load subscription data from the API
     * @param {string} hostname - The hostname to load subscription for
     * @param {number} planId - The plan ID
     * @return {Promise<void>}
     * @private
     */
    async _loadSubscriptionData(
      hostname: string,
      planId: number
    ): Promise<void> {
      try {
        this.status.isLoading = true;
        this.error.loading = null;

        const [subscription, ActiveUserCount] = await Promise.all([
          loadSubscription(hostname),
          getActiveUserCount(hostname, planId),
        ]);

        this.data[hostname] = { ...subscription, ActiveUserCount };

        // Emit subscription data to legacy frontend
        await this.emitSubscriptionToLegacy(hostname, this.data[hostname]);
      } catch (e) {
        this.error.loading = e as Error;
        throw e;
      } finally {
        this.status.isLoading = false;
      }
    },

    /**
     * Emits subscription data to the legacy frontend via event bus
     * @param {string} hostname - The hostname the subscription data belongs to
     * @param {any} subscription - The subscription data to emit
     * @return {void}
     */
    async emitSubscriptionToLegacy(
      hostname: string,
      subscription: any
    ): Promise<void> {
      const eventBus = useEventBus();
      await eventBus.initialize();
      eventBus.$emit(EVENTS.SUBSCRIPTION_LOADED, {
        hostname,
        subscription,
      });
    },

    /**
     * Clears cached subscription data and loading promises
     * @param {string} [hostname] - Optional hostname to clear cache for specific tenant.
     *                              If not provided, clears all cached data.
     * @return {void}
     */
    clearCache(hostname?: string): void {
      if (hostname) {
        delete this.data[hostname];
        delete this.loadingPromises[hostname];
      } else {
        this.data = {};
        this.loadingPromises = {};
      }
    },
    /**
     * Gets the subscription status for a specific add-on
     *
     * @param {string} hostname - The hostname to check
     * @param {string} addOnId - The add-on ID to check
     * @return {string | null} The subscription status or null if not found
     */
    getAddOnSubscriptionStatus(
      hostname: string,
      addOnId: string
    ): string | null {
      if (!this.data[hostname]?.addOnProducts) {
        return null;
      }
      return this.data[hostname]?.addOnProducts[addOnId]?.status || null;
    },
    /**
     * Gets the plan name for a specific hostname
     *
     * @param {string} hostname - The hostname to check
     * @return {string} The plan name or 'Inactive' if not found
     */
    getPlanName(hostname: string): string {
      const planId = this.data[hostname]?.SubscriptionType;
      return SUBSCRIPTION_NAME[planId] || 'Inactive';
    },
    /**
     * Gets feature flags for a specific hostname
     *
     * @param {string} hostname - The hostname to check
     * @param {string} featureFlagKey - The feature flag key to check
     * @return {boolean} Whether the feature flag is enabled
     */
    getFeatureFlags(hostname: string, featureFlagKey: string): boolean {
      const subscription = this.data[hostname];
      return subscription?.featureFlags[featureFlagKey] || false;
    },
    getPlanNameTracking(
      planId: keyof typeof SUBSCRIPTION_NAME_TRACKING
    ): string {
      return SUBSCRIPTION_NAME_TRACKING[planId] || 'UNKNOWN';
    },
    /**
     * Gets the active user count for a specific hostname
     *
     * @param {string} hostname - The hostname to check
     * @return {number} The active user count
     */
    getActiveUserCount(hostname: string): number {
      return this.data[hostname]?.ActiveUserCount || 0;
    },
    getAddOnTrialEndDateISOString(
      hostname: string,
      addOnId: keyof typeof addOns
    ): string {
      const now = new Date();
      const trialLength = this.getTrialLength(addOnId);
      const trialEndDate =
        this.data[hostname]?.addOnProducts?.[addOnId]?.trialEndDate;
      const endDate = new Date(
        trialEndDate ?? addDaysToDate(now, trialLength.count)
      );

      return endDate.toISOString();
    },
    getAddOnTrialEndDate(
      hostname: string,
      addOnId: keyof typeof addOns
    ): string {
      return formatDisplayDateShort(
        this.getAddOnTrialEndDateISOString(hostname, addOnId)
      );
    },
    getAddOnTerminationDate(hostname: string, addOnId: string): string | null {
      return (
        this.data[hostname]?.addOnProducts[addOnId]?.terminationDate || null
      );
    },
    getAddOnTrialDaysRemaining(hostname: string, addOnId: string): number {
      return (
        getAddOnTrialDaysRemaining(
          this.data[hostname]?.addOnProducts[addOnId]?.trialEndDate
        ) || 0
      );
    },
    isAddOnTrialAvailable(hostname: string, addOnId: string): boolean {
      if (this.data[hostname]?.addOnProducts) {
        return this.data[hostname]?.addOnProducts[addOnId]?.trialAvailable;
      }

      return false;
    },
    isFlexi(planId: number): boolean {
      return planId == SUBSCRIPTION.FLEXI;
    },
    isMobileOnly(planId: number): boolean {
      return planId == SUBSCRIPTION.MOBILE_ONLY;
    },
    getAddOnSubscriptionType(
      hostname: string,
      addOnId: string
    ): ADD_ON_SUBSCRIPTION_TYPE | null {
      const addOn = this.data[hostname]?.addOnProducts?.[addOnId];

      if (this.isAnnualAddOnActive(hostname, addOnId)) {
        return ADD_ON_SUBSCRIPTION_TYPE.ANNUAL_ACTIVE;
      }

      if (this.isAddOnMonthlyActiveAndCanMoveToYearly(hostname, addOnId)) {
        return ADD_ON_SUBSCRIPTION_TYPE.ANNUAL_INACTIVE;
      }

      if (addOn?.status === ADD_ON_STATUS.ACTIVE) {
        const onceStore = useOnceStore();
        if (onceStore.isUsPayroll(addOnId) && this.isPreImplementation(addOn)) {
          return ADD_ON_SUBSCRIPTION_TYPE.PRE_IMPLEMENTATION_ACTIVE;
        }
        return ADD_ON_SUBSCRIPTION_TYPE.PAID_ACTIVE;
      }

      if (this.isAddOnTrialActive(hostname, addOnId)) {
        return ADD_ON_SUBSCRIPTION_TYPE.TRIAL;
      }

      if (this.isCancelledAddOnStillActive(hostname, addOnId)) {
        return ADD_ON_SUBSCRIPTION_TYPE.CANCELLED_ACTIVE;
      }

      return null;
    },
    isAnnualAddOnActive(hostname: string, addOnId: string): boolean {
      const annualAddOnId =
        this.data[hostname]?.addOnProducts?.[addOnId]
          ?.equivalentPaymentFrequencyPlans?.annual;
      const annualPaidMonthlyAddOnId =
        this.data[hostname]?.addOnProducts?.[addOnId]
          ?.equivalentPaymentFrequencyPlans?.annualPaidMonthly;

      if (!annualAddOnId && !annualPaidMonthlyAddOnId) {
        return false;
      }

      const annualAddOn = annualAddOnId
        ? this.data[hostname]?.addOnProducts?.[annualAddOnId]
        : undefined;
      const annualPaidMonthlyAddOn = annualPaidMonthlyAddOnId
        ? this.data[hostname]?.addOnProducts?.[annualPaidMonthlyAddOnId]
        : undefined;

      return (
        annualAddOn?.status === ADD_ON_STATUS.ACTIVE ||
        annualPaidMonthlyAddOn?.status === ADD_ON_STATUS.ACTIVE
      );
    },

    /**
     * Checks if an add-on is currently active on a monthly plan and eligible to be moved to a yearly plan.
     *
     * @param {string} hostname - The hostname of the tenant.
     * @param {string} addOnId - The identifier of the add-on to check.
     * @return {boolean} A boolean indicating whether the add-on is monthly active and can be moved to yearly.
     *
     * This function performs the following checks:
     * 1. Verifies if the add-on exists for the given hostname.
     * 2. Checks if an equivalent annual plan exists for the add-on.
     * 3. Ensures the annual add-on is not marked as ineligible.
     * 4. Confirms that the current add-on status is active.
     *
     * If all conditions are met, it returns true, indicating the add-on can be moved to a yearly plan.
     */
    isAddOnMonthlyActiveAndCanMoveToYearly(
      hostname: string,
      addOnId: string
    ): boolean {
      const onceStore = useOnceStore();
      if (onceStore.isUsPayroll(addOnId)) {
        return false;
      }

      const addOn = this.data[hostname]?.addOnProducts?.[addOnId];
      if (!addOn) {
        return false;
      }

      const annualAddOnId = addOn?.equivalentPaymentFrequencyPlans?.annual;
      const annualPaidMonthlyAddOnId =
        addOn?.equivalentPaymentFrequencyPlans?.annualPaidMonthly;

      // Return false if no annual add-on is available
      if (!annualAddOnId && !annualPaidMonthlyAddOnId) {
        return false;
      }

      const annualAddOn = annualAddOnId
        ? this.data[hostname]?.addOnProducts?.[annualAddOnId]
        : undefined;
      const annualPaidMonthlyAddOn = annualPaidMonthlyAddOnId
        ? this.data[hostname]?.addOnProducts?.[annualPaidMonthlyAddOnId]
        : undefined;
      if (!annualAddOn && !annualPaidMonthlyAddOn) {
        return false;
      }

      // Check if ANY annual add-on is eligible (not ineligible)
      const hasEligibleAnnualAddOn =
        (annualAddOn && annualAddOn.status !== ADD_ON_STATUS.INELIGIBLE) ||
        (annualPaidMonthlyAddOn &&
          annualPaidMonthlyAddOn.status !== ADD_ON_STATUS.INELIGIBLE);

      if (!hasEligibleAnnualAddOn) {
        return false;
      }

      // At least one annual add-on is eligible, so check if monthly add-on is active
      return addOn.status === ADD_ON_STATUS.ACTIVE;
    },
    isAddOnTrialActive(hostname: string, addOnId: string): boolean {
      const addOn = this.data[hostname]?.addOnProducts?.[addOnId];
      const trialDaysRemaining = getAddOnTrialDaysRemaining(
        addOn?.trialEndDate
      );

      return (
        !!addOn &&
        typeof trialDaysRemaining === 'number' &&
        trialDaysRemaining > 0 &&
        addOn.status === ADD_ON_STATUS.ACTIVE
      );
    },
    isPreImplementation(addOn: AddOnProduct): boolean {
      if (!addOn.startBillingDate) {
        return false;
      }
      const startBilling = new Date(addOn.startBillingDate);
      const now = new Date();
      return startBilling.getTime() > now.getTime();
    },
    isCancelledAddOnStillActive(hostname: string, addOnId: string): boolean {
      const terminationDate =
        this.getCancelledAddOnTerminationDate(hostname, addOnId) || '';
      return terminationDate.length > 0;
    },
    // Only returns cancelled termination date of an add-on if it's still active
    getCancelledAddOnTerminationDate(
      hostname: string,
      addOnId: string
    ): string | null {
      const addOn = this.data[hostname]?.addOnProducts?.[addOnId];
      if (!addOn || addOn.status !== ADD_ON_STATUS.CANCELLED) {
        return null;
      }

      const terminationDateStr = this.getLatestTerminationDateOfAddOns(
        hostname,
        addOnId
      );
      if (!terminationDateStr) {
        return null;
      }

      const terminationDate = new Date(terminationDateStr);
      const now = new Date();

      return terminationDate.getTime() > now.getTime()
        ? terminationDateStr
        : null;
    },
    getLatestTerminationDateOfAddOns(
      hostname: string,
      addOnId: string
    ): string | null {
      // Retrieve termination date from all equivalent add-ons and get whichever is the most recent
      let latestTerminationDateStr = this.getAddOnTerminationDate(
        hostname,
        addOnId
      );

      const addOn = this.data[hostname]?.addOnProducts?.[addOnId];
      const equivalentAddOnIds = Object.values(
        addOn?.equivalentPaymentFrequencyPlans || {}
      );

      // Retrieve termination date of equivalent add-ons and compare with the current add-on termination date
      equivalentAddOnIds?.forEach((equivalentAddOnId: string) => {
        const equivalentAddOnTerminationDateStr = this.getAddOnTerminationDate(
          hostname,
          equivalentAddOnId
        );
        if (!equivalentAddOnTerminationDateStr) {
          return;
        }

        if (
          !latestTerminationDateStr ||
          new Date(equivalentAddOnTerminationDateStr).getTime() >
            new Date(latestTerminationDateStr).getTime()
        ) {
          latestTerminationDateStr = equivalentAddOnTerminationDateStr;
        }
      });

      return latestTerminationDateStr;
    },
    getCurrentActiveAddOns(hostname: string): string[] {
      const addOns = this.data[hostname]?.addOnProducts;

      if (!addOns) {
        return [];
      }

      return Object.keys(addOns).filter(
        addOnIdentifier =>
          addOns[addOnIdentifier]?.status === ADD_ON_STATUS.ACTIVE
      );
    },
    getCurrentActiveAndCancelledActiveAddOns(hostname: string): string[] {
      const addOns = this.data[hostname]?.addOnProducts;

      if (!addOns) {
        return [];
      }

      const activeAndCancelledAddOns = Object.keys(addOns).filter(
        addOnIdentifier => {
          return (
            addOns[addOnIdentifier]?.status === ADD_ON_STATUS.ACTIVE ||
            (addOns[addOnIdentifier]?.status === ADD_ON_STATUS.CANCELLED &&
              this.getAddOnSubscriptionType(hostname, addOnIdentifier) ===
                ADD_ON_SUBSCRIPTION_TYPE.CANCELLED_ACTIVE)
          );
        }
      );

      // Sort the add-ons according to the defined ordering
      return useAddOnStore().sortAddOnsByOrdering(activeAndCancelledAddOns);
    },
    getCurrentActiveAndCancelledActiveWithFreeAddOns(
      hostname: string
    ): string[] {
      const addOns = this.data[hostname]?.addOnProducts;

      if (!addOns) {
        return [];
      }

      const addOnStore = useAddOnStore();
      const currentPlan = useOnceStore().plan;

      return Object.keys(addOns).filter(addOnIdentifier => {
        const isAddOnFreeWithPlan = addOnStore.isFreeWithPlan(
          addOnIdentifier,
          currentPlan
        );

        // Include add-ons that are either active or free with the current plan
        return (
          addOns[addOnIdentifier]?.status === ADD_ON_STATUS.ACTIVE ||
          (addOns[addOnIdentifier]?.status === ADD_ON_STATUS.CANCELLED &&
            this.getAddOnSubscriptionType(hostname, addOnIdentifier) ===
              ADD_ON_SUBSCRIPTION_TYPE.CANCELLED_ACTIVE) ||
          isAddOnFreeWithPlan
        );
      });
    },
    /**
     * Reduces each add-on to the single frequency variant that will keep billing.
     *
     * A frequency switch leaves both variants live for the rest of the term: the outgoing one
     * is CANCELLED but still active, the incoming one is ACTIVE. The UI renders one row per
     * add-on and groups it by frequency, so the outgoing variant must not decide the group.
     *
     * @param {string} hostname The tenant to read add-on statuses for.
     * @param {string[]} addOnIds Add-on identifiers, typically several variants of the same add-on.
     * @return {string[]} One identifier per add-on: the ACTIVE variant, else the annual one.
     */
    collapseToOngoingFrequencyVariant(
      hostname: string,
      addOnIds: string[]
    ): string[] {
      const addOnStore = useAddOnStore();
      const variantsByAddOn = new Map<string, string[]>();

      addOnIds.forEach(addOnId => {
        // Variants of one add-on share a parent. The payload is authoritative: a family the
        // static catalogue does not carry would otherwise group alone and skip the collapse.
        const key =
          this.data[hostname]?.addOnProducts?.[addOnId]?.parentAddOn ||
          allAddOnDetails[addOnId]?.parentAddOn ||
          addOnId;
        const variants = variantsByAddOn.get(key);

        if (variants) {
          variants.push(addOnId);
        } else {
          variantsByAddOn.set(key, [addOnId]);
        }
      });

      return Array.from(variantsByAddOn.values()).map(variants => {
        if (variants.length === 1) {
          return variants[0];
        }

        // Order first so the choice never depends on addOnProducts key order.
        const ordered = addOnStore.sortAddOnsByOrdering(variants);

        const ongoing = ordered.find(
          addOnId =>
            this.getAddOnSubscriptionStatus(hostname, addOnId) ===
            ADD_ON_STATUS.ACTIVE
        );

        if (ongoing) {
          return ongoing;
        }

        // Nothing is billing on: every variant is cancelled and still inside its term. Keep
        // the annual one, which is the group the row sat in before the collapse existed.
        return (
          ordered.find(addOnId => addOnStore.isAnnualAddOn(addOnId)) ??
          ordered[0]
        );
      });
    },
    hasActiveAnnualAddOns(hostname: string): boolean {
      const addOns = this.data[hostname]?.addOnProducts;

      if (!addOns) {
        return false;
      }

      // Check if any add-on has an active annual subscription
      return Object.keys(addOns).some(addOnIdentifier => {
        return this.isAnnualAddOnActive(hostname, addOnIdentifier);
      });
    },
    openPurchaseAdditionalUsersModal(model: any): void {
      this.status.isPurchaseAdditionalUsersModalVisible = true;
      if (model) {
        this.status.subscriptionModel = model;
      }
    },
    closePurchaseAdditionalUsersModal(): void {
      this.status.isPurchaseAdditionalUsersModalVisible = false;
      this.status.isSuccessSwitchToAnnualModalVisible = false;
    },
    openSuccessPurchaseAdditionalUsersModal(
      isSwitchToAnnual: boolean | null
    ): void {
      this.status.isSuccessPurchaseAdditionalUsersModalVisible = true;
      if (isSwitchToAnnual) {
        this.status.isSuccessSwitchToAnnualModalVisible = true;
      }
    },
    closeSuccessPurchaseAdditionalUsersModal(): void {
      this.status.isSuccessPurchaseAdditionalUsersModalVisible = false;
      this.status.isSuccessSwitchToAnnualModalVisible = false;
    },
    resetSubscriptionModel() {
      this.status.subscriptionModel = {};
    },
    openRenewAnnualSubscriptionModal(model: any): void {
      this.status.isRenewAnnualSubscriptionModalVisible = true;
      if (model) {
        this.status.subscriptionModel = model;
      }
    },
    closeRenewAnnualSubscriptionModal(): void {
      this.status.isRenewAnnualSubscriptionModalVisible = false;
    },
    openSuccessRenewAnnualSubscriptionModal(): void {
      this.status.isSuccessRenewAnnualSubscriptionModalVisible = true;
    },
    closeSuccessRenewAnnualSubscriptionModal(): void {
      this.status.isSuccessRenewAnnualSubscriptionModalVisible = false;
    },
    openChoosePlanModal(): void {
      this.status.isChoosePlanModalVisible = true;
    },
    closeChoosePlanModal(): void {
      this.status.isChoosePlanModalVisible = false;
    },
    getAddOnVisibilityState(
      hostname: string,
      addOnId: string
    ): 'express_interest' | 'eligible' | 'hidden' | '' | null {
      return (
        this.data[hostname]?.addOnProducts?.[addOnId]?.visibilityState ?? null
      );
    },
    getAddOnExpressInterestUrl(
      hostname: string,
      addOnId: string
    ): string | null {
      return (
        this.data[hostname]?.addOnProducts?.[addOnId]?.expressInterestUrl ??
        null
      );
    },
  },
});
